import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { establishmentsAPI } from '../../services/api';
import { FiPlus, FiSearch, FiGrid } from 'react-icons/fi';
import { Skeleton, SkeletonTable } from '../../design-system';
import EstablishmentForm from '../../components/establishments/EstablishmentForm';
import EstablishmentTable from '../../components/establishments/EstablishmentTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const EstablishmentManagement = () => {
  const { isMobile, addNotification } = useApp();
  const [establishments, setEstablishments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showEstablishmentForm, setShowEstablishmentForm] = useState(false);
  const [selectedEstablishment, setSelectedEstablishment] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadEstablishments();
  }, []);

  const loadEstablishments = async () => {
    try {
      setIsLoading(true);
      const establishmentsData = await establishmentsAPI.getAll();
      setEstablishments(establishmentsData);
    } catch (error) {
      console.error('Error loading establishments:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar los establecimientos'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateEstablishment = () => {
    setSelectedEstablishment(null);
    setShowEstablishmentForm(true);
  };

  const handleEditEstablishment = (establishment) => {
    setSelectedEstablishment(establishment);
    setShowEstablishmentForm(true);
  };

  const handleDeleteEstablishment = (establishment) => {
    setDeleteConfirm(establishment);
  };

  const confirmDeleteEstablishment = async () => {
    try {
      await establishmentsAPI.delete(deleteConfirm.id);
      setEstablishments(prevEstablishments =>
        prevEstablishments.filter(e => e.id !== deleteConfirm.id)
      );
      setDeleteConfirm(null);

      addNotification({
        type: 'success',
        message: 'Establecimiento eliminado exitosamente'
      });
    } catch (error) {
      console.error('Error deleting establishment:', error);
      addNotification({
        type: 'error',
        message: 'Error al eliminar el establecimiento'
      });
    }
  };

  const handleEstablishmentSaved = async (establishmentData) => {
    try {
      if (selectedEstablishment) {
        // Update existing establishment
        const updatedEstablishment = await establishmentsAPI.update(
          selectedEstablishment.id,
          establishmentData
        );
        setEstablishments(prevEstablishments =>
          prevEstablishments.map(e =>
            e.id === selectedEstablishment.id ? updatedEstablishment : e
          )
        );
        addNotification({
          type: 'success',
          message: 'Establecimiento actualizado exitosamente'
        });
      } else {
        // Create new establishment
        const newEstablishment = await establishmentsAPI.create(establishmentData);
        setEstablishments(prevEstablishments => [...prevEstablishments, newEstablishment]);
        addNotification({
          type: 'success',
          message: 'Establecimiento creado exitosamente'
        });
      }

      setShowEstablishmentForm(false);
      setSelectedEstablishment(null);
    } catch (error) {
      console.error('Error saving establishment:', error);
      addNotification({
        type: 'error',
        message: 'Error al guardar el establecimiento'
      });
    }
  };

  const getFilteredEstablishments = () => {
    return establishments.filter(establishment => {
      const matchesSearch = !searchTerm ||
        establishment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        establishment.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        establishment.city?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = !statusFilter ||
        (statusFilter === 'active' && establishment.is_active) ||
        (statusFilter === 'inactive' && !establishment.is_active);

      return matchesSearch && matchesStatus;
    });
  };

  const getEstablishmentStats = () => {
    const total = establishments.length;
    const active = establishments.filter(e => e.is_active).length;
    const inactive = total - active;

    return { total, active, inactive };
  };

  const filteredEstablishments = getFilteredEstablishments();
  const stats = getEstablishmentStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          Gestión de Establecimientos
        </h1>
        <button
          onClick={handleCreateEstablishment}
          className="btn btn-primary flex items-center space-x-2"
        >
          <FiPlus className="h-4 w-4" />
          <span>Nuevo Establecimiento</span>
        </button>
      </div>

      {/* Stats with Skeleton */}
      {isLoading ? (
        <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <Skeleton variant="circle" width="48px" height="48px" />
                <div className="ml-4 flex-1 space-y-2">
                  <Skeleton variant="text" width="50%" height="14px" />
                  <Skeleton variant="text" width="40%" height="24px" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md p-3 bg-blue-100">
                <FiGrid className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md p-3 bg-green-100">
                <span className="text-green-600 font-bold">✓</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Activos</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.active}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md p-3 bg-red-100">
                <span className="text-red-600 font-bold">✗</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Inactivos</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.inactive}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
          {/* Search */}
          <div className="relative col-span-2">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Buscar establecimientos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10"
            />
          </div>

          {/* Status filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="">Todos los estados</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </div>
        </div>

        {/* Results count */}
        {!isLoading && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Mostrando {filteredEstablishments.length} de {establishments.length} establecimientos
            </p>

            {(searchTerm || statusFilter) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                }}
                className="btn btn-secondary btn-sm"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        )}
      </div>

      {/* Establishments Table with Skeleton */}
      {isLoading ? (
        <SkeletonTable rows={8} columns={5} showActions />
      ) : (
        <EstablishmentTable
          establishments={filteredEstablishments}
          onEdit={handleEditEstablishment}
          onDelete={handleDeleteEstablishment}
          onRefresh={loadEstablishments}
        />
      )}

      {/* Establishment Form Modal */}
      {showEstablishmentForm && (
        <EstablishmentForm
          establishment={selectedEstablishment}
          onSave={handleEstablishmentSaved}
          onClose={() => {
            setShowEstablishmentForm(false);
            setSelectedEstablishment(null);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <ConfirmDialog
          title="Eliminar Establecimiento"
          message={`¿Estás seguro de que deseas eliminar el establecimiento "${deleteConfirm.name}"? Esta acción eliminará también todos los datos relacionados y no se puede deshacer.`}
          confirmText="Eliminar"
          confirmStyle="danger"
          onConfirm={confirmDeleteEstablishment}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

export default EstablishmentManagement;